// src/components/MainCanvas.jsx
import React, { useContext, useEffect, useState, useRef, useMemo } from 'react';
import {
	DeckContext,
	getEdgeColor,
	DeckBoxEdgeTypes,
	BumpoutEdgeTypes,
	StairEdgeTypes,
	getEdgeBorderColor,
	LandingEdgeTypes,
} from '../context/DeckContext';
import DeckBox from './blocks/DeckBox';
import {
	Box,
	Typography,
	ButtonGroup,
	IconButton,
	Divider,
} from '@mui/material';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Add, Remove, Restore } from '@mui/icons-material';
import BlockType from '../../../../../enums/blockType';
import Stair from './blocks/Stair';
import Landing from './blocks/landing';
import BlockModal from './popUps/BlockModalV2.tsx';
import Bumpout from './blocks/Bumpout';
import EdgeModal from './popUps/EdgeModalV2.tsx';
import ScaleGridBox from './ScaleGridBox';
import { calculateCanvasBounds } from '../utils/calculateCanvasBounds';
import { useQuoteState } from 'src/stateProviders/QuoteStateProvider.tsx';
import DropbeamNeededModal from './popUps/dropbeamNeededModal';
import DropbeamNotNeededModal from './popUps/dropbeamNotNeededModal';

const MainCanvas = () => {
	const { state, dispatch, transformWrapperRef } = useContext(DeckContext);
	const [isDragging, setIsDragging] = useState(false);
	const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
	const quoteState = useQuoteState();

	// Track blocks array reference changes
	const prevBlocksRef = useRef(state.blocks);
	useEffect(() => {
		if (prevBlocksRef.current !== state.blocks) {
			prevBlocksRef.current = state.blocks;
		}
	}, [state.blocks]);

	// Calculate canvas bounds dynamically
	const canvasBounds = useMemo(() => {
		return calculateCanvasBounds(state.blocks, state.scaleFactor);
	}, [state.blocks, state.scaleFactor]);

	useEffect(() => {}, []);

	// Update canvas bounds in context
	useEffect(() => {
		dispatch({ type: 'SET_CANVAS_BOUNDS', payload: canvasBounds });
	}, [canvasBounds, dispatch, state.blocks.length]);

	const deckboxes = state.blocks.filter((blk) => blk.type == BlockType.DECKBOX);
	const landings = deckboxes.flatMap((dk) => dk.landings);
	const stairs = deckboxes.flatMap((dk) => dk.stairs);
	const bumpouts = deckboxes.flatMap((dk) => dk.bumpouts);

	useEffect(() => {
		if (isDragging) {
			dispatch({ type: 'CLOSE_BLOCK_MODAL' });
			dispatch({
				type: 'SELECT_EDGE',
				payload: { blockId: null, edgeLabel: null },
			});
		}
	}, [isDragging]);

	const handleSelect = (block) => {
		if (state.selectedEdge?.blockId) {
			return;
		}

		dispatch({ type: 'SELECT_BLOCK', payload: block });

		if (state.displayedBlockModal?.id === block.id) {
			dispatch({ type: 'CLOSE_BLOCK_MODAL' });
			setIsDragging(false); // Reset dragging state
		} else {
			dispatch({ type: 'OPEN_BLOCK_MODAL', payload: block });
		}
	};

	const handleUpdateBlock = (updatedBlock) => {
		dispatch({ type: 'UPDATE_BLOCK', payload: updatedBlock });
	};

	const renderEdgeLegend = () => {
		const { selectedBlock } = state;

		if (!selectedBlock) return null;

		let edgeTypes = [];
		switch (selectedBlock.type) {
			case 'DeckBox':
				edgeTypes = DeckBoxEdgeTypes;
				break;
			case 'Bumpout':
				edgeTypes = BumpoutEdgeTypes;
				break;
			case 'Stair':
				edgeTypes = StairEdgeTypes;
				break;
			case 'Landing':
				edgeTypes = LandingEdgeTypes;
				break;
			default:
				edgeTypes = [];
		}

		return (
			<Box
				sx={{
					width: '100%',
					position: 'absolute',
					top: '6px',
					left: '0px',
					zIndex: '20',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					gap: '15px',
				}}
			>
				<Typography
					sx={{
						color: '#A9A9A9',
						fontSize: '13px',
						fontWeight: '600',
						fontFamily: 'Inter',
					}}
				>
					Railing Indicators
				</Typography>

				<Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
					{edgeTypes.map((type) => (
						<Box
							key={type}
							sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
						>
							<Box
								sx={{
									width: 45,
									height: 10,
									borderRadius: '1px',
									backgroundColor: getEdgeColor(type),
									...(getEdgeBorderColor(type) && {
										border: `1.5px solid ${getEdgeBorderColor(type)}`,
									}),
								}}
							/>
							<Typography
								fontWeight="600"
								fontSize="13px"
								color="#505050"
								fontFamily="Inter"
							>
								{type}
							</Typography>
						</Box>
					))}
				</Box>
			</Box>
		);
	};

	return (
		<Box
			id="main-canvas"
			flex="1"
			position="relative"
			bgcolor="#f0f0f0"
			overflow="auto"
			sx={{
				width: '100%',
				height: '100%',
				touchAction: 'manipulation',
				// Scrollbar styles - visible on all devices including tablets
				'&::-webkit-scrollbar': {
					width: '10px',
					height: '10px',
					WebkitAppearance: 'none',
				},
				'&::-webkit-scrollbar-track': {
					background: 'rgba(0, 0, 0, 0.05)',
					borderRadius: '5px',
				},
				'&::-webkit-scrollbar-thumb': {
					background: 'rgba(0, 0, 0, 0.3)',
					borderRadius: '5px',
					'&:hover': {
						background: 'rgba(0, 0, 0, 0.5)',
					},
				},
				'& .react-transform-wrapper': {
					overflow: 'visible !important',
					width: '100%',
					height: '100%',
					minWidth: '100%',
					minHeight: '100%',
					touchAction: 'manipulation',
				},
				'& .react-transform-component': {
					overflow: 'visible !important',
					width: '100%',
					height: '100%',
					minWidth: '100%',
					minHeight: '100%',
					touchAction: 'manipulation',
				},
			}}
		>
			<ScaleGridBox
				scaleFactor={state.scaleFactor}
				zoomScale={state.zoomScale}
			/>

			{(quoteState.reface ||
				(quoteState.structure?.materialType &&
					quoteState.structure?.joistSpacing)) && (
				<>
					<TransformWrapper
						ref={transformWrapperRef}
						initialScale={1}
						minScale={0.3333}
						maxScale={2}
						wheel={{ disabled: true }}
						doubleClick={{ disabled: true }}
						panning={{ disabled: isDragging }}
						limitToBounds
						centerOnInit={false}
						pinch={{ disabled: false }}
						pan={{ disabled: isDragging || isTouchDevice }}
						zoomAnimation={{ disabled: false }}
						onTransformed={(ref, transformState) => {
							console.log('[MainCanvas] TransformComponent zoom/pan:', {
								scale: transformState.scale,
								positionX: transformState.positionX,
								positionY: transformState.positionY,
								note: 'CSS transform handles all scaling - container stays at base size',
							});
							dispatch({
								type: 'SET_ZOOM_SCALE',
								payload: transformState.scale,
							});
						}}
					>
						{({ zoomIn, zoomOut, resetTransform }) => (
							<>
								{/* Zoom Controls */}
								{state.blocks.length > 0 && (
									<Box position="absolute" top={10} left={10} zIndex={1000}>
										<ButtonGroup
											orientation="vertical"
											variant="contained"
											size="small"
											sx={{
												backgroundColor: 'white',
												'.MuiIconButton-root': {
													borderRadius: 0,
													'&:hover': {
														backgroundColor: 'rgba(0, 0, 0, 0.08)',
													},
													'&:active': {
														backgroundColor: 'rgba(0, 0, 0, 0.16)',
													},
												},
												'.MuiIconButton-root.Mui-focusVisible': {
													backgroundColor: 'rgba(0, 0, 0, 0.08)',
												},
											}}
										>
											<IconButton
												onClick={() => zoomIn(0.18)}
												size="small"
												disableRipple
												aria-label="Zoom In"
											>
												<Add />
											</IconButton>
											<Divider orientation="horizontal" />
											<IconButton
												onClick={() => zoomOut(0.18)}
												size="small"
												disableRipple
												aria-label="Zoom Out"
											>
												<Remove />
											</IconButton>
											<Divider orientation="horizontal" />
											<IconButton
												onClick={() => resetTransform()}
												size="small"
												disableRipple
												aria-label="Reset"
											>
												<Restore />
											</IconButton>
										</ButtonGroup>
									</Box>
								)}

								{renderEdgeLegend()}

								<TransformComponent>
									<Box
										sx={{
											position: 'relative',
											width: `${canvasBounds.width}px`,
											height: `${canvasBounds.height}px`,
										}}
									>
										<Box
											sx={{
												position: 'absolute',
												top: 0,
												left: 0,
												width: '100%',
												height: '100%',
												transform: `translate(${canvasBounds.offsetX}px, ${canvasBounds.offsetY}px)`,
											}}
											ref={(el) => {
												if (el) {
													console.log(
														'[MainCanvas] Blocks container transform applied:',
														{
															offsetX: canvasBounds.offsetX,
															offsetY: canvasBounds.offsetY,
															transform: `translate(${canvasBounds.offsetX}px, ${canvasBounds.offsetY}px)`,
														}
													);
												}
											}}
										>
											{state.blocks.map((block) => {
												console.log('[MainCanvas render] Rendering block:', {
													blockId: block.id,
													blockLabel: block.label,
													position: block.position,
													type: block.type,
												});
												if (block.type === 'DeckBox') {
													return (
														<DeckBox
															key={block.id}
															label={block.label}
															block={block}
															onSelect={handleSelect}
															onUpdateBlock={handleUpdateBlock}
															setIsDragging={setIsDragging}
														/>
													);
												}
												return null;
											})}

											{/* Render Stairs */}
											{stairs.map((stair) => (
												<Stair
													key={stair.id}
													label={stair.label}
													stair={stair}
													onSelect={handleSelect}
													onUpdateBlock={handleUpdateBlock}
													setIsDragging={setIsDragging}
												/>
											))}

											{/* Render Landings */}
											{landings.map((landing) => (
												<Landing
													key={landing.id}
													label={landing.label}
													landing={landing}
													onSelect={handleSelect}
													onUpdateBlock={handleUpdateBlock}
													setIsDragging={setIsDragging}
												/>
											))}

											{/* Render Bumpouts */}
											{bumpouts.map((bumpout) => (
												<Bumpout
													key={bumpout.id}
													label={bumpout.label}
													bumpout={bumpout}
													onSelect={handleSelect}
													onUpdateBlock={handleUpdateBlock}
													setIsDragging={setIsDragging}
												/>
											))}
										</Box>
									</Box>
								</TransformComponent>
							</>
						)}
					</TransformWrapper>

					{/* Render Block Modal */}
					{state.displayedBlockModal && (
						<BlockModal block={state.displayedBlockModal} />
					)}

					{/* Render Edge Modal */}
					{state.selectedEdge?.blockId && state.selectedEdge?.edgeLabel && (
						<EdgeModal />
					)}

					{state.dropbeamNeeded?.show && (
						<DropbeamNeededModal
							open={state.dropbeamNeeded.show}
							block={state.dropbeamNeeded.block}
							neededDropbeams={state.dropbeamNeeded.dropbeamsNeeded}
							setOpen={(value) => {
								dispatch({
									type: 'SET_DROPBEAM_NEEDED',
									payload: {
										...state.dropbeamNeeded,
										show: value,
									},
								});
							}}
						/>
					)}

					{state.dropbeamNotNeeded?.show && (
						<DropbeamNotNeededModal
							open={state.dropbeamNotNeeded.show}
							block={state.dropbeamNotNeeded.block}
							setOpen={(value) => {
								dispatch({
									type: 'SET_DROPBEAM_NOT_NEEDED',
									payload: {
										...state.dropbeamNotNeeded,
										show: value,
									},
								});
							}}
						/>
					)}
				</>
			)}

			{!quoteState.reface &&
				!(
					quoteState.structure?.materialType &&
					quoteState.structure?.joistSpacing
				) && (
					<Box
						sx={{
							width: '100%',
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'center',
							gap: '10px',
						}}
					>
						<Typography
							sx={{
								fontWeight: 'bold',
							}}
						>
							Select Joist Material & Spacing Above
						</Typography>
						<Typography>
							To begin building your deck, you need to select the Structural
							Material first, in order to continue
						</Typography>
					</Box>
				)}
		</Box>
	);
};

export default MainCanvas;
